// @flow

type UndoRedoStackOptions = {
    item: any
}

/**
 * The `UndoRedoStack` object allows an app to track, undo or redo user actions.
 * The undo action has to start from the top and move to the bottom of the stack sequentially.
 * The redo action has to start from the bottom and move to the top of the stack sequentially.
 * Pushing a new item to the stack may remove undone items. For example, the stack has total 5 item and 4 items have been undone.
 * If a new item is pushed in, the 4 undone items will be removed from the stack. The stack will just have item 1 and the newly added one.
 * The assumption is that the current state is satisfying. There is no need to track previous undone items after adding a new one.
 *
 * You createa a `UndoRedoStack` by specifying a baseline item.
 *
 * @param {Object} options
 * @example
 * const undoRedoStack = new UndoRedoStack({
 *   item: [...stops]
 * });
 */

class UndoRedoStack {
    // This property will be used when undo the last item at the bottom in the stack.
    _baselineItem: any;

    // This is used to track app action sequence items such as stops for route drag 1, stops for route drag 2.
    _data: Array<any>;

    // The index number of this._data where undo or redo happens.
    _positionPointer: number;

    constructor(options: UndoRedoStackOptions) {
        this._baselineItem = options.item;
        this._data = [];
        this._positionPointer = -1;
    }

    /**
     * Pushes data item to a stack.
     *
     * @param {any} item Data item
     * @example
     * undoRedoStack.push(stops);
     */
    push(item: any) {
        this._positionPointer++;
        const deleteCount = this.size() - this._positionPointer;
        this._data.splice(this._positionPointer, deleteCount, item);
    }

    /**
     * Gets the number of items in the stack.
     * @returns {number} the length of the stack
     */
    size() {
        return this._data.length;
    }

    /**
     * Can undo
     * @returns {boolean} returns true if can undo, otherwise false
     */
    canUndo() {
        return this._positionPointer >= 0;
    }

    /**
     * Can redo
     * @returns {boolean} returns true if can redo, otherwise false
     */
    canRedo() {
        if (this.size() === 0)
            return false;
        return this._positionPointer < this.size() - 1;
    }

    /**
     * Gets the data item that can be used to perform undo.
     * @returns {any} data item
     */
    undo() {
        if (this.canUndo()) {
            return this._getData(--this._positionPointer);
        }
        return null;
    }

    /**
     * Gets the data item that can be used to perform redo.
     *  @returns {any} data item
     */
    redo() {
        if (this.canRedo()) {
            return this._getData(++this._positionPointer);
        }
        return null;
    }

    _getData(_positionPointer: number) {
        const item = _positionPointer >= 0 ? this._data[_positionPointer] : this._baselineItem;
        return item;
    }
}

export default UndoRedoStack;
